// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useContext } from 'react';
import { Action, Checkbox, InputError, Option, RadioGroup } from '@op/opds.opux';
import { ContextWrapper, ContractData } from '../../context/ContextWrapper';
import message from '../../messages';
import { termsAndConditionsUrl, id } from '../../Constants';
import { isValidAcceptTermsInput } from '../../validation/Validation';
import AdditionalSignatory from './AdditionalSignatory';

const Confirmation: React.FC = () => {
    const { contractData, updateInformation, isAdminFlow } = useContext(ContextWrapper);

    const modifyState = (variable: keyof ContractData, data: any): void => {
        updateInformation((state: any) => {
            return { ...state, [variable]: data };
        });
    };

    const onTermsAndConditions = (): void => {
        const lang = document.documentElement.getAttribute('lang');
        const langValue = lang === null ? 'fi' : lang.substring(0, 2).toLowerCase();
        window.open(termsAndConditionsUrl.get(langValue), '_blank');
    };

    const confirmSignatory = { ...contractData.confirmSignatory };
    const checkValidation = contractData.checkValidation;
    const checkBoxValidation = isValidAcceptTermsInput(confirmSignatory.acceptTerms, checkValidation);

    return (
        <div id={'agreementSection'} className="opux-spacing-bottom-margin-double" data-testid="agreement-section">
            <h3
                className="opux-spacing-top-margin-double opux-font-weight-demibold"
                data-testid="agreement-section-title"
            >
                {message('agreementSectionTitleText')}
            </h3>
            <div
                id={'signatoryNotification'}
                className="opux-spacing-bottom-margin-double opux-spacing-top-margin"
                data-testid="signatory-notification"
            >
                <div className="opux-font-weight-light" data-testid="signatory-notification-text">
                    {isAdminFlow
                        ? message('agreementSigningRightsTextIsAdminFlow')
                        : message('agreementSigningRightsText')}
                </div>
                <div
                    className="opux-spacing-top-margin opux-font-weight-light"
                    data-testid="multiple-signatories-instructions"
                >
                    {message('multipleSignatoriesInstructions')}
                </div>
            </div>
            <Checkbox
                id={id.APPROVE_TERMS}
                checked={confirmSignatory.acceptTerms}
                labelProps={{ className: 'check-box-label' }}
                invalid={checkBoxValidation.notValid}
                onChange={(_env: any, checked: boolean) => {
                    confirmSignatory.acceptTerms = checked;
                    modifyState('confirmSignatory', confirmSignatory);
                }}
                data-testid="approve-terms-checkbox"
            >
                <span>
                    {message('agreementUnderstandingPartOne')}
                    <Action
                        className="terms-link"
                        inline
                        onClick={() => onTermsAndConditions()}
                        data-testid="terms-link"
                    >
                        {message('termsAndConditions')}
                    </Action>
                    {message('agreementUnderstandingPartTwo')}
                </span>
            </Checkbox>
            <InputError error={message(checkBoxValidation.message)} data-testid="input-error" />
            <h3
                className="opux-spacing-top-margin-double
                            opux-spacing-bottom-margin-double opux-font-weight-demibold"
                data-testid="e-signature-required"
            >
                {message('eSignatureRequired')}
            </h3>
            <RadioGroup
                id="tupasSigning"
                buttons
                value={confirmSignatory.selectedSigning}
                onChange={(_ev: any, value: string) => {
                    confirmSignatory.selectedSigning = value;
                    modifyState('confirmSignatory', confirmSignatory);
                }}
                data-testid="tupas-signing-radio-group"
            >
                <Option id="tupas" value="tupas" data-testid="tupas-option">
                    {message('tupasOption')}
                </Option>
                <Option id="mail" value="mail" data-testid="mail-option">
                    {message('mailOption')}
                </Option>
            </RadioGroup>
            {confirmSignatory.selectedSigning === 'tupas' && <AdditionalSignatory />}
        </div>
    );
};

export default Confirmation;
